import React from "react";
import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import type { Recording } from "../../api/types";
import RecordingNameEditor from "./RecordingNameEditor";

type RecordingTitleControlProps = {
  recording?: Pick<Recording, "id" | "name"> | null;
  title: string;
  onRename?: (id: string, name: string | null) => Promise<void>;
  variant?: "list" | "detail";
  className?: string;
};

export const RecordingTitleControl = ({
  recording,
  title,
  onRename,
  variant = "list",
  className,
}: RecordingTitleControlProps) => {
  const content =
    recording && onRename ? (
      <RecordingNameEditor
        recording={recording}
        title={title}
        onRename={onRename}
        variant={variant}
      />
    ) : variant === "detail" ? (
      <h1 className="min-w-0 max-w-full truncate text-2xl font-bold text-text font-display">
        {title}
      </h1>
    ) : (
      <span className="min-w-0 max-w-full truncate text-sm font-medium text-text">{title}</span>
    );

  return className ? <div className={className}>{content}</div> : <>{content}</>;
};

type RecordingActionControlsProps = {
  recording?: Pick<Recording, "id" | "include_in_processes"> | null;
  archiveBusy?: boolean;
  /** Keep the action slots visible but disabled while the next session loads. */
  loading?: boolean;
  onArchiveChange?: (id: string, archived: boolean) => void | Promise<void>;
  onDelete?: () => void;
  size?: "sm" | "md";
};

export const RecordingActionControls = ({
  recording,
  archiveBusy = false,
  loading = false,
  onArchiveChange,
  onDelete,
  size = "md",
}: RecordingActionControlsProps) => {
  const { t } = useTranslation();
  const canArchive = !!onArchiveChange && (!!recording || loading);

  if (!canArchive && !onDelete) {
    return null;
  }

  const isArchived = recording ? !recording.include_in_processes : false;
  const ArchiveIcon = isArchived ? ArchiveRestore : Archive;
  const archiveTitle = isArchived
    ? t("unarchive_recording_tooltip")
    : t("archive_recording_tooltip");
  const defaultButtonSize = size === "sm" ? "h-8 w-8" : "h-9 w-9";

  return (
    <div className="flex shrink-0 items-center gap-2">
      {canArchive && (
        <button
          type="button"
          disabled={archiveBusy || loading}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            if (recording && onArchiveChange) void onArchiveChange(recording.id, !isArchived);
          }}
          className={clsx(
            "relative z-20 inline-flex shrink-0 items-center justify-center rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30 disabled:cursor-not-allowed disabled:opacity-60",
            defaultButtonSize,
            isArchived
              ? "text-text-dim hover:bg-info/10 hover:text-info-strong"
              : "text-text-dim hover:bg-muted-surface hover:text-text",
          )}
          title={archiveTitle}
          aria-label={archiveTitle}
        >
          <ArchiveIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          disabled={loading}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onDelete();
          }}
          className={clsx(
            "relative z-20 flex items-center justify-center rounded-lg bg-transparent text-danger transition hover:bg-danger/10 hover:text-danger-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/30 disabled:cursor-not-allowed disabled:opacity-60",
            defaultButtonSize,
          )}
          title={t("delete_recording")}
          aria-label={t("delete_recording")}
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
};
